<template>
  <div
      class="relative mx-1 mt-1 rounded px-1"
      :class="organizations?.length > 1 ? 'hover:bg-cinder-800' : ''"
    >
    <SidebarMenu>
      <SidebarMenuItem>
         <Tooltip
                :tooltip-trigger-class="'transition duration-200'"
                :placement="'right'"
                :show-tooltip="!isSmallScreen && !sidebarOpen"
            >
        <template #trigger>
        <DropdownMenu>
          <DropdownMenuTrigger
            v-if="organizations?.length > 1"
            as-child
        >
            <SidebarMenuButton
                size="lg"
                class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
                <div class="mr-2 flex h-8 w-8 rounded-full bg-slate-600 text-white aspect-square" width="32" height="32">
                    <AppIcon
                        fixed-width
                        name="building"
                        class="m-auto h-4 w-4 shrink-0"
                    />
                </div>
                <div v-if="activeOrganization" class="grid flex-1 text-left text-sm leading-tight">
                    <span class="truncate font-semibold">{{ activeOrganization.name }}</span>
                    <!-- <span class="truncate text-xs">{{ getSubscriptionPlan }}</span> -->
                </div>
                <ChevronsUpDown class="ml-auto" />
            </SidebarMenuButton>
        </DropdownMenuTrigger>

          <SidebarMenuButton
            v-else
            size="lg"
            as="div"
            class="pointer-events-none cursor-default !hover:bg-transparent !focus:bg-transparent hover:!bg-transparent focus:!bg-transparent"
            style="background-color: transparent !important"
          >
            <div class="mr-2 flex h-8 w-8 rounded-full bg-slate-600 text-white aspect-square" width="32" height="32">
              <AppIcon
                fixed-width
                name="building"
                class="m-auto h-4 w-4 shrink-0"
              />
            </div>
            <div v-if="activeOrganization" class="grid flex-1 text-left text-sm leading-tight">
              <span class="truncate font-semibold">{{ activeOrganization.name }}</span>
              <!-- <span class="truncate text-xs">{{ getSubscriptionPlan }}</span> -->
            </div>
          </SidebarMenuButton>

          <!-- DropdownMenuContent always mounted -->
          <DropdownMenuContent
              class="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg border-[#27272a] bg-cinder-900 text-white"
              align="start"
              :side="isSmallScreen ? 'bottom' : 'right'"
              :side-offset="4"
          >
              <DropdownMenuLabel class="sticky text-xs text-white/90"> Organizations </DropdownMenuLabel>

              <div
                  v-if="organizations?.length > 1"
                  class="sticky top-[-0px] z-50 bg-cinder-900 pb-2 pl-2 pr-2 pt-2"
                  @click.stop
              >
                  <input
                      ref="searchInput"
                      v-model="searchText"
                      class="w-full border border-[#27272a] bg-cinder-800 text-sm text-muted-foreground text-white hover:ring-1 hover:ring-cinder-600 focus:border-[#27272a] focus:outline-none focus:ring-1 focus:ring-cinder-600 rounded"
                      type="text"
                      maxlength="100"
                      placeholder="Search for organization"
                      @blur="onInputBlur"
                      @click.stop
                  />
              </div>

              <div
                  class="max-h-[45vh] overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-700"
                  @pointermove.stop
              >
                  <DropdownMenuItem
                      v-for="organization in filteredOrganizations"
                      :key="organization?.id"
                      class="flex cursor-pointer rounded-md px-3 py-2 text-sm text-white hover:bg-cinder-800"
                      variant="unstyled"
                      @click="onOrganizationClick(organization)"
                  >
                      <div class="flex items-center gap-2">
                          <div class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-600 text-white">
                              <AppIcon
                                  fixed-width
                                  name="building"
                                  class="h-4 w-4"
                              />
                          </div>
                          <span class="truncate">{{ organization.name }}</span>
                      </div>
                  </DropdownMenuItem>
              </div>

              <DropdownMenuSeparator class="bg-[#27272a]" />

              <div
                  v-if="isSfStaff"
                  class="flex cursor-pointer items-center gap-2 rounded-md p-2 text-white transition-colors hover:bg-cinder-800"
                  @pointermove.stop
                  @click.prevent="onCreateOrganization"
              >
                  <div class="flex size-6 items-center justify-center rounded-md bg-cinder-900">
                      <Plus class="size-4" />
                  </div>
                  <div class="text-sm text-white">Create organization</div>
              </div>
          </DropdownMenuContent>
        </DropdownMenu>
          </template>
            {{ activeOrganization.name }}
        </Tooltip>
      </SidebarMenuItem>
    </SidebarMenu>
  </div>
</template>

<script setup>
import _ from 'lodash'
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { useStore } from 'vuex'
import { ChevronsUpDown, Plus } from 'lucide-vue-next'
import { useRouter, useRoute } from 'vue-router'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar'
import Tooltip from '@/components/Tooltip.vue'
import { useIsSmallScreen } from './isSmallScreen.composable'

const dropdownOpen = ref(false)
const searchInput = ref(null)
const store = useStore()
const router = useRouter()
const route = useRoute()
const searchText = ref('')

const props = defineProps({
    sidebarOpen: Boolean,
})

const { isSmallScreen } = useIsSmallScreen()

// Close dropdown programmatically
const closeDropdown = () => {
    dropdownOpen.value = false
    searchText.value = ''
}

// Handle ESC key to close dropdown
const keyHandler = ({ keyCode }) => {
    if (!dropdownOpen.value || keyCode !== 27) return
    closeDropdown()
}

onMounted(() => {
    document.addEventListener('keydown', keyHandler)
})

onUnmounted(() => {
    document.removeEventListener('keydown', keyHandler)
})

const onCreateOrganization = () => {
    store.commit('modal:open', 'create_organization')
    closeDropdown()
}

const onOrganizationClick = async (organization) => {
    store.commit('self:activeOrganizationId', {
        id: organization.id,
    })

    closeDropdown()

    nextTick(() => {
        if (route.name === 'no-access') {
            router.push({
                name: 'landing',
            })
        } else {
            router.push({
                name: route.name,
                query: route.query,
            })
        }
    })
}

const organizations = computed(() => store.getters.selfOrganizations)

const filteredOrganizations = computed(() => {
    if (!searchText.value && store.getters.selfActiveOrganization.name === 'Streamforge') {
        return []
    }

    if (!searchText.value) {
        return _(store.getters.selfOrganizations)
            .filter((organization) => {
                return organization.name === 'Streamforge'
            })
            .value()
    }

    return _(store.getters.selfOrganizations)
        .filter((organization) => {
            return organization.name.toLowerCase().includes(searchText.value.toLowerCase())
        })
        .value()
})

const activeOrganization = computed(() => store.getters.selfActiveOrganization)
const isSfStaff = computed(() => store.getters.isSfStaff)

function onInputBlur() {
  nextTick(() => searchInput.value?.focus())
}
</script>
